import { Cart as GqlCart } from '@app/data/cart-bff';
import { Cart as MonolithCart } from '@app/types/monolith/cart';
import logger from '@redbubble/next/logger';
import { mapCouponType } from '../../types/monolith/utils';
import { intlErrorMessages as PayPalMessages } from './.';
import { ErrorMessage, PayPalRequiredCart } from './types';

type HandlerParams = {
  onError: (message: ErrorMessage) => void;
  metricName: string;
  message?: ErrorMessage;
};

const localeMap: Record<string, string> = {
  de: 'de_DE',
  en: 'en_US',
  fr: 'fr_FR',
  es: 'es_ES',
  xx: 'en_US',
  default: 'en_US',
};

export const getPaypalLocale = (cartLocale: string) =>
  localeMap[cartLocale] || localeMap.default;

export const getMerchantAccountId = (currency: string) =>
  `redbubbleinc${currency.toUpperCase()}`;

export const mapCartToPayPalRequiredCartFromGQL = (
  cart: GqlCart
): PayPalRequiredCart => {
  const selectedShippingMethod =
    cart.summary.shipping.selected.method.toLowerCase() as
      | 'standard'
      | 'express';

  return {
    summary: {
      subtotal: cart.summary.subtotal.amount,
      total: cart.summary.total.amount,
      estimated_shipping: {
        preference: selectedShippingMethod,

        standard: cart.summary.shipping.estimated.standard.price.amount,
        standard_ex_tax: cart.summary.shipping.estimated.standard.amountExTax,

        express: cart.summary.shipping.estimated.express
          ? cart.summary.shipping.estimated.express.price.amount
          : null,
        express_ex_tax: cart.summary.shipping.estimated.express
          ? cart.summary.shipping.estimated.express.amountExTax
          : null,
      },
      tax: cart.summary.tax
        ? {
            amount: cart.summary.tax.amount,
            type: cart.summary.tax.type,
          }
        : null,
      show_tax_separately: cart.summary.showTaxSeparately,
    },
    items: cart.lineItems.map((item) => ({
      quantity: item.quantity,
    })),
    applied_coupon: cart.appliedCoupon
      ? {
          amount: cart.appliedCoupon.discount.amount.toString(),
          type: mapCouponType(cart.appliedCoupon.type),
          code: cart.appliedCoupon.code,
        }
      : null,
  };
};

export const mapCartToPayPalRequiredCartFromMonolith = (
  cart: MonolithCart
): PayPalRequiredCart => {
  const { items, applied_coupon } = cart;
  const {
    total,
    tax,
    show_tax_separately,
    estimated_shipping: {
      standard,
      standard_ex_tax,
      express,
      express_ex_tax,
      preference,
    },
    subtotal_ex_tax,
    subtotal,
  } = cart.summary;

  const appliedCoupon = applied_coupon
    ? {
        amount: applied_coupon.sub_discount.amount,
        type: applied_coupon.type,
        code: applied_coupon.code,
      }
    : null;

  const subtotalAmount = show_tax_separately ? subtotal_ex_tax : subtotal || 0;

  return {
    summary: {
      subtotal: subtotalAmount,
      total,
      tax,
      show_tax_separately,
      estimated_shipping: {
        preference,
        standard,
        standard_ex_tax,
        express,
        express_ex_tax,
      },
    },
    items: items.map((item) => ({ quantity: item.quantity })),
    applied_coupon: appliedCoupon,
  };
};

const errorPayload = (step: string, metricName: string) => {
  return {
    err: { step },
    context: 'PayPalExpress',
    message: `${metricName}::${step}`,
  };
};

export const onPayPalInitError = ({ metricName }: HandlerParams) => {
  const step = 'paypal-init';
  logger.info(errorPayload(step, metricName), `${metricName}::${step}`);
  // We don't show an error on init because the user hasn't interacted with a payment method yet. The PayPal button
  // won't show and there's nothing they can do other than try a different payment method (if available), so calling
  // their attention to the error will just be confusing and unhelpful.
};

export const onPayPalCreateOrderError = ({
  message,
  onError,
  metricName,
}: HandlerParams) => {
  const step = 'paypal-create';
  logger.info(errorPayload(step, metricName), `${metricName}::${step}`);
  onError(message || PayPalMessages.serverError);
};

export const onPayPalApproveOrderError = ({
  message,
  onError,
  metricName,
}: HandlerParams) => {
  const step = 'paypal-approve';
  logger.info(errorPayload(step, metricName), `${metricName}::${step}`);

  onError(message || PayPalMessages.serverError);
};
